import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { API } from '@/api';
import { RESERVABLE_TIME_UNIT_IN_MINUTES, SPACE_TYPE } from '@/constants/space';
import { useDateTimeFormat } from '@/hooks/useDateTimeFormat';

export type TimeSlot =
  API['GET']['ShowerRoomAvailableTimeSlots']['response']['time_slots'][number];

interface GroupedTimeSlot {
  date: string;
  hours: Array<{
    hour: number;
    slots: TimeSlot[];
  }>;
}

interface TimeSlotPickerProps {
  timeSlots: TimeSlot[];
  onTimeSelect: (startDateTime: string, durationMinutes: number) => void;
  selectedDatetime?: string;
}

/**
 * 時間スロット選択UIコンポーネント（個人端末のシャワールーム用）
 */
export const PersonalDeviceTimeSlotPicker = ({
  timeSlots,
  onTimeSelect,
  selectedDatetime,
}: TimeSlotPickerProps) => {
  const { t } = useTranslation();
  const { formatDateWithWeekdayLong, formatHourLabel } = useDateTimeFormat();

  // 時刻から時間（H時）を取得
  const getHourFromTime = (time: string): number => {
    const [hour] = time.split(':');
    return parseInt(hour, 10);
  };

  // 日付と時間でグループ化
  // displayable=falseのスロットのみの時間帯は除外
  const groupedSlots = useMemo(() => {
    const grouped: GroupedTimeSlot[] = [];

    timeSlots.forEach((slot) => {
      const slotDate = slot.date;
      const slotHour = getHourFromTime(slot.time);

      // 日付グループを探す
      let dateGroup = grouped.find((g) => g.date === slotDate);
      if (!dateGroup) {
        dateGroup = {
          date: slotDate,
          hours: [],
        };
        grouped.push(dateGroup);
      }

      // 時間グループを探す
      let hourGroup = dateGroup.hours.find((h) => h.hour === slotHour);
      if (!hourGroup) {
        hourGroup = {
          hour: slotHour,
          slots: [],
        };
        dateGroup.hours.push(hourGroup);
      }

      hourGroup.slots.push(slot);
    });

    // displayable=trueのスロットが1つもない時間帯を除外
    return grouped
      .map((dateGroup) => ({
        ...dateGroup,
        hours: dateGroup.hours.filter((hourGroup) =>
          hourGroup.slots.some((slot) => slot.displayable !== false),
        ),
      }))
      .filter((dateGroup) => dateGroup.hours.length > 0); // 時間帯が1つもない日付も除外
  }, [timeSlots]);

  if (timeSlots.length === 0) {
    return null;
  }

  return (
    <>
      <div
        className="reservation-card__info reservation-card__info--emphasis"
        dangerouslySetInnerHTML={{
          __html: t('reservation.showerRoom.durationNote'),
        }}
      />
      {groupedSlots.map((dateGroup) => (
        <fieldset key={dateGroup.date} className="date-picker__day">
          <legend className="date-picker__legend">
            <div className="date-picker__date">
              {formatDateWithWeekdayLong(dateGroup.date)}
            </div>
          </legend>
          <div className="date-picker__list">
            {dateGroup.hours.map((hourGroup) => (
              <div key={hourGroup.hour} className="date-picker__group">
                <div className="date-picker__group-label">
                  {formatHourLabel(hourGroup.hour)}
                </div>
                <div className="date-picker__group-bt">
                  {hourGroup.slots.map((slot) => {
                    const isSelected = selectedDatetime === slot.datetime;
                    const isDisplayable = slot.displayable !== false;
                    return (
                      <button
                        key={slot.time}
                        type="button"
                        disabled={!slot.available}
                        onClick={() =>
                          slot.available &&
                          onTimeSelect(
                            slot.datetime,
                            RESERVABLE_TIME_UNIT_IN_MINUTES[
                              SPACE_TYPE.SHOWER_ROOM
                            ],
                          )
                        }
                        className={`date-picker__slot ${
                          !isDisplayable
                            ? 'date-picker__slot--hidden'
                            : slot.available
                              ? `date-picker__slot--available${isSelected ? ' is-selected' : ''}`
                              : 'date-picker__slot--unavailable'
                        }`}
                        style={
                          !isDisplayable ? { visibility: 'hidden' } : undefined
                        }
                      >
                        {slot.time}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </fieldset>
      ))}
    </>
  );
};
